import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import useProductCategories from "@/hooks/useProductCategories";
import useProductCategoryMutations from "@/hooks/useProductCategoryMutations";
import { Row } from "@tanstack/react-table";

function ProductCategoriesPage() {
  const { data, isLoading } = useProductCategories();
  const [name, setName] = React.useState("");
  const [openAdd, setOpenAdd] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);

  const { createMutation, updateMutation, deleteMutation } =
    useProductCategoryMutations({
      resetForm: () => setName(""),
      closeAdd: () => setOpenAdd(false),
      closeEdit: () => setOpenEdit(false),
      clearEditingId: () => setEditingId(null),
      closeConfirm: () => setOpenConfirm(false),
    });

  function startEdit(row: { id: number; name: string }) {
    setEditingId(row.id);
    setName(row.name);
    setOpenEdit(true);
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({ name: name.trim() });
  }

  function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !editingId) return;
    updateMutation.mutate({ id: editingId, name: name.trim() });
  }

  function triggerDelete() {
    if (editingId) {
      const idToDelete = editingId;
      setEditingId(null); // Clear immediately to prevent double calls
      deleteMutation.mutate(idToDelete);
    }
  }

  const columns = [
    { accessorKey: "id", header: "ID" },
    { accessorKey: "name", header: "Category Name" },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: { row: Row<{ id: number; name: string }> }) => (
        <ActionsMenu
          onEdit={() => startEdit(row.original)}
          onDelete={() => {
            setEditingId(row.original.id);
            setOpenConfirm(true);
          }}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Product Categories</h1>
      <div className="flex items-center justify-between">
        <p>Manage all product categories.</p>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button>+ Add Product Category</Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate} className="space-y-3">
              <DialogHeader>
                <DialogTitle>Add Product Category</DialogTitle>
                <DialogDescription>
                  Enter the name of the new product category.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                <label htmlFor="pcName">Product Category Name</label>
                <Input
                  id="pcName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || !name.trim()}
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <DataTable
        columns={columns as any}
        data={(data as any) || []}
        placeholder="Filter Product Categories"
        filterColumns={["name"]}
      />
      <ConfirmDialog
        open={openConfirm}
        onOpenChange={(o) => {
          if (!o) {
            setEditingId(null);
          }
          setOpenConfirm(o);
        }}
        isLoading={deleteMutation.isPending}
        handleConfirm={() => triggerDelete()}
        AlertdialogDescription="This will permanently delete this product category. Are you sure?"
      />
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Product Category</DialogTitle>
            <DialogDescription>
              Update the name of the product category.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-3">
            <div className="grid gap-2">
              <label htmlFor="pcNameEdit">Product Category Name</label>
              <Input
                id="pcNameEdit"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={updateMutation.isPending || !name.trim()}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductCategoriesPage;
